import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState, CliffRules } from '@somnhold/sim';
import { cliffRulesH, simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A crack in the rock: a jagged split. */
const CRACK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2l3 4-2 3 4 3-1 2" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 3l2 3-1 2 2 3" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round" opacity=".6"/></svg>';
/** A coil of rope. */
const ROPE = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="8" rx="6" ry="3.4" fill="none" stroke="currentColor" stroke-width="1.5"/><ellipse cx="8" cy="8" rx="3.4" ry="1.8" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M13.5 9c1 2 0 4-2 5" stroke="#ff4f3a" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
/** A ledge: a shelf in the rock, stepping down. */
const LEDGE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 3h6v3h4v3h4v4" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linejoin="round"/></svg>';
/** Sela's anchor: a ring on a stake. */
const ANCHOR = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="4.5" r="2.6" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 7v7M5 14h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
/** A shrieker: leather wings, no eyes. */
const BAT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 6c-1.5 0-2 1.6-2 3l-5-3c0 3 2 5 4 5l3 2 3-2c2 0 4-2 4-5l-5 3c0-1.4-.5-3-2-3z" fill="currentColor"/><path d="M6.6 6.2l-.6-2 1.4 1.4M9.4 6.2l.6-2-1.4 1.4" stroke="currentColor" stroke-width="1"/></svg>';
/** A hollow stone ringing. */
const STONE = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="6" cy="10" rx="4" ry="3.6" fill="currentColor"/><circle cx="6" cy="9.6" r="1" fill="#16181c"/><path d="M11 6a4 4 0 0 1 0 6M13 4a7 7 0 0 1 0 10" stroke="#ff4f3a" stroke-width="1.3" fill="none" stroke-linecap="round"/></svg>';
/** The mother's shriek: a ring growing. */
const RING = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2.4 1.6"/><circle cx="8" cy="8" r="2" fill="#ff4f3a"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; at: number; point: THREE.Vector3; x: number; y: number };

/**
 * The flank's cliff on screen (docs/sendas/explorador.md, chapter 2), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the cornice (`edge`): Sela's anchor going in (the act's clock as blows of the hammer), the shriekers out and where
 *   they go (the last sound).
 * - the wall and the night: the rope (what it is doing: aiming, going down, hanging from a creaking crack and its
 *   count, out of reach), the ledge, the shriekers and the stone ringing, the mother's shriek coming; over each crack in
 *   sight a mark (tap: shoot the rope at it, or go by the rope left on it) that says only what it sounded like; over
 *   each hollow stone a mark (tap: ring it).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules, at the
 * wall's height (render's `cliffRulesH`).
 */
export class GaloHud {
  readonly panel: HTMLElement;
  private act: 'edge' | 'wall' | 'night';
  private v = new THREE.Vector3();
  private key = '';
  private marks: Mark[] = [];
  private ropeRow = h('div.bh-row.al-waverow.gh-rope');
  private ropeText = h('span.al-wave');
  private ledgeRow = h('div.bh-row.gh-ledge');
  private ledgeText = h('span.al-wave');
  private anchorRow = h('div.bh-row.al-waverow.gh-anchor');
  private anchorText = h('span.al-wave');
  private anchorBar = h('span.gh-bar', {}, h('i'));
  private batRow = h('div.bh-row.al-waverow.gh-bats');
  private batText = h('span.al-wave');
  private motherRow = h('div.bh-row.al-waverow.gh-mother');
  private motherText = h('span.al-wave');
  private hx = 0;
  private hy = 0;

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.cliff as CliffRules;
    this.act = r.act;
    const rows: HTMLElement[] = [];
    if (r.act === 'edge') {
      this.anchorRow.append(h('span.bh-ico', { html: ANCHOR }), h('span.bh-label', {}, t('senda.galo.anchor')), this.anchorBar, this.anchorText);
      this.batRow.append(h('span.bh-ico', { html: BAT }), this.batText);
      rows.push(this.anchorRow, this.batRow);
    } else {
      this.ropeRow.append(h('span.bh-ico', { html: ROPE }), h('span.bh-label', {}, t('senda.galo.rope')), this.ropeText);
      this.ledgeRow.append(h('span.bh-ico', { html: LEDGE }), this.ledgeText);
      rows.push(this.ropeRow, this.ledgeRow);
      if (r.act === 'night') {
        this.batRow.append(h('span.bh-ico', { html: BAT }), this.batText);
        this.motherRow.append(h('span.bh-ico', { html: RING }), h('span.bh-label', {}, t('senda.galo.mother')), this.motherText);
        rows.push(this.batRow, this.motherRow);
      }
      r.cracks.forEach((c, i) => this.addMark(s, r, i, c.x, c.y, 0.5, CRACK, 'senda.galo.crackHint', `crack-${i}`));
      r.stones.forEach((st, i) => this.addMark(s, r, r.cracks.length + i, st.x, st.y, 1.1, STONE, 'senda.galo.stoneHint', `stone-${i}`));
    }
    this.panel = h(`div.bh.gh-panel.gh-${r.act}`, {}, ...rows);
  }

  private addMark(s: BattleState, r: CliffRules, at: number, x: number, y: number, lift: number, glyph: string, hint: string, test: string): void {
    const st = h('small.al-state.gh-state', {}, '');
    const el = h(
      `button.al-stone.gh-mark.gh-${test.split('-')[0]}`,
      { type: 'button', 'data-test': `galo-${test}`, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: glyph }),
      st,
    ) as HTMLButtonElement;
    this.board.append(el);
    const point = simToWorld(x, y, new THREE.Vector3(), cliffRulesH(s, r, x, y) + lift);
    this.marks.push({ el, state: st, at, point, x, y });
  }

  private setState(m: Mark, text: string): void {
    if (m.state.textContent === text) return;
    m.state.textContent = text;
    remeasureMark(m.el);
  }

  private text(el: HTMLElement, v: string): void {
    if (el.textContent !== v) el.textContent = v;
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.cliff;
    const w = s.senda?.cliff;
    if (!r || !w) return;
    const hero = s.heroes[0];
    if (hero) {
      this.hx = hero.x;
      this.hy = hero.y;
    }
    const sec = (ticks: number): number => Math.max(1, Math.ceil(ticks / TICK_HZ));
    const out = w.shriekers.length;
    if (r.act === 'edge' && r.edge) {
      const k = Math.min(1, s.waveT / r.edge.time);
      const blows = Math.min(20, Math.floor((s.waveT / r.edge.hammer) | 0));
      this.text(this.anchorText, t('senda.galo.anchorLeft', { n: blows, of: Math.floor(r.edge.time / r.edge.hammer), s: sec(Math.max(0, r.edge.time - s.waveT)) }));
      setStyle(this.anchorBar.firstChild as HTMLElement, 'width', `${Math.round(k * 100)}%`);
      this.text(this.batText, out ? t('senda.galo.batsTo', { n: out }) : t('senda.galo.batsNone'));
      this.batRow.classList.toggle('warn', out > 0);
      return;
    }
    // The rope: what it is doing.
    const crack = w.job >= 0 && w.job < r.cracks.length ? w.job : -1;
    const rope = w.trip
      ? t('senda.galo.down')
      : w.hang >= 0
        ? t('senda.galo.hang', { s: sec(Math.max(0, w.gives - s.tick)) })
        : w.shotAt >= 0
          ? t('senda.galo.aim')
          : crack >= 0
            ? t('senda.galo.toSpot')
            : t('senda.galo.tap');
    this.text(this.ropeText, rope);
    this.ropeRow.classList.toggle('warn', w.hang >= 0);
    const goal = r.ledges.findIndex((l) => l.goal);
    const lv = w.on >= 0 ? (r.ledges[w.on]?.level ?? 0) : -1;
    const levels = Math.max(...r.ledges.map((l) => l.level)) + 1;
    this.text(this.ledgeText, w.on === goal ? t(r.act === 'night' ? 'senda.galo.foot' : 'senda.galo.aster') : lv >= 0 ? t('senda.galo.ledge', { n: lv + 1, of: levels }) : t('senda.galo.onRope'));
    if (r.act === 'night' && r.night) {
      const ringing = w.stone.i >= 0 && s.tick < w.stone.until;
      this.text(this.batText, ringing ? t('senda.galo.stoneRings', { s: sec(w.stone.until - s.tick) }) : out ? t('senda.galo.batsTo', { n: out }) : t('senda.galo.batsNone'));
      this.batRow.classList.toggle('warn', out > 0 && !ringing);
      this.batRow.classList.toggle('good', ringing);
      const m = w.mother;
      const mt = m.id === 0 ? t('senda.galo.motherOff') : m.id < 0 ? t('senda.galo.motherDown') : m.ring >= 0 ? t('senda.galo.motherShriek', { s: sec(m.ring + r.night.mother.warn - s.tick) }) : t('senda.galo.motherNext', { s: sec(Math.max(0, m.next - s.tick)) });
      this.text(this.motherText, mt);
      this.motherRow.classList.toggle('warn', m.ring >= 0);
      this.motherRow.classList.toggle('good', m.id < 0);
    }
    const key = `${w.sounded.join('')}|${w.ropes.length}|${w.job}|${w.hang}|${w.on}|${w.done}|${w.stone.i}|${w.rung.join('')}|${s.tick < w.stone.until}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        if (m.at < r.cracks.length) {
          const c = r.cracks[m.at];
          const known = !!w.sounded[m.at];
          const roped = w.ropes.some((x) => x.crack === m.at);
          this.setState(m, roped ? t('senda.galo.roped') : known ? t(c?.kind === 0 ? 'senda.galo.firm' : c?.kind === 1 ? 'senda.galo.hollow' : 'senda.galo.crumb') : '');
          m.el.classList.toggle('firm', known && c?.kind === 0);
          m.el.classList.toggle('hollow', known && c?.kind === 1);
          m.el.classList.toggle('crumb', known && c?.kind === 2);
          m.el.classList.toggle('hung', w.hang === m.at);
        } else {
          const si = m.at - r.cracks.length;
          this.setState(m, w.stone.i === si && s.tick < w.stone.until ? t('senda.galo.ringing') : w.rung[si] ? t('senda.galo.cracked') : '');
          m.el.classList.toggle('hollow', !!w.rung[si] && !(w.stone.i === si && s.tick < w.stone.until));
        }
      }
    }
    for (const m of this.marks) {
      m.el.classList.toggle('sent', w.job === m.at);
      m.el.disabled = s.result !== 'running' || !!w.trip;
    }
  }

  /** Every frame: the marks over their places, those near the hero only (the wall is tall: the rest would crowd it). */
  place(camera: THREE.Camera, w: number, ht: number, s?: BattleState | null): void {
    const r = s?.rules?.senda?.cliff;
    const reach = (r?.rope ?? 6000) * 1.35;
    for (const m of this.marks) {
      // (A hollow stone shows when it is on the hero's ledge, near or not: ringing it far off is the point.)
      const stone = m.at >= (r?.cracks.length ?? 0);
      const far = !stone && (m.x - this.hx) * (m.x - this.hx) + (m.y - this.hy) * (m.y - this.hy) > reach * reach;
      const hollow = m.el.classList.contains('hollow');
      if (far || hollow || (r?.act === 'night' && m.at >= (r?.cracks.length ?? 0) && r.stones[m.at - r.cracks.length]?.ledge !== s?.senda?.cliff?.on)) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
